import { yupResolver } from "@hookform/resolvers/yup";
import { Button, FormControlLabel, Grid, Paper, Radio, Stack } from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import _ from "lodash";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import CustomLoader from "../../../../../util/component/CustomLoader";
import SectionHeader from "../../../../../util/component/SectionHeader";
import { setSnackbar } from "../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../util/state/globalVariables";
import { createRule, getAncillaries, getCarGroups, getCategories, getLocations, getProducts, getRates, getSegments, getSalesChannels } from "../../../api/services";
import { FormCheckbox, FormDateTimePicker, FormNumberInput, FormRadioGroup, FormTextField } from "../../../components/fields";
import { ILocation, IRule, Nullable, ReactQueryKey, RuleType } from "../../../types";
import { dayjsDateTime, LOCALE_DATE_TIME_FORMAT } from "../../../utils";
import { ruleValidationSchema } from "../form";
import { IFormValuesRule, RuleConstraint } from "../types";
import { getDefaultConstraints } from "../utils";
import ConstraintList from "../_components/constraint-list/ConstraintList";
import {RulesProvider, useRulesContext} from "../_context/RulesContext";
import { MailingList } from "../_rule_fields";
import TenantForm from "../../../components/multitenant/TenantForm";
import DealerForm from "../../../components/multitenant/DealerForm";

const InsertRulePage = () => {

    const { t } = useTranslation('booking');

    const location = useLocation();
    const locationState = (location.state as Nullable<{ rule: IRule | undefined }>);

    const { dispatchGlobalData, globalData } = useContext(GlobalContext);
    const userHasMultipleTenants = globalData?.user.legalEntities.length > 1 && globalData?.user.dealers.length > 1

    const navigate = useNavigate();

    const form = useForm<IFormValuesRule>({
        defaultValues: {
            type: locationState?.rule?.type ?? RuleType.Pending,
            name: locationState?.rule?.name ?? '',
            bookingDateFrom: locationState?.rule?.startDateTime ? dayjs(locationState.rule.startDateTime) : dayjsDateTime(),
            bookingDateTo: locationState?.rule?.endDateTime ? dayjs(locationState.rule.endDateTime) : null,
            skip: locationState?.rule?.skip,
            applyFor: locationState?.rule?.applyFor === null ? 0 : locationState?.rule?.applyFor,
            mailingList: locationState?.rule?.mailingList,
            alertOnSetting: locationState?.rule?.alertOnSetting,
            alertOnActivation: locationState?.rule?.alertOnActivation,
            pickupDateTimeFrom: {
                value: locationState?.rule?.pickupDateTimeFrom ? dayjs(locationState.rule.pickupDateTimeFrom) : null,
                excluded: locationState?.rule?.excludedPickupDateTimeFrom
            },
            pickupDateTimeTo: {
                value: locationState?.rule?.pickupDateTimeTo ? dayjs(locationState.rule.pickupDateTimeTo) : null,
                excluded: locationState?.rule?.excludedPickupDateTimeTo
            },
            dropoffDateTimeFrom: {
                value: locationState?.rule?.dropoffDateTimeFrom ? dayjs(locationState.rule.dropoffDateTimeFrom) : null,
                excluded: locationState?.rule?.excludedDropoffDateTimeFrom
            },
            dropoffDateTimeTo: {
                value: locationState?.rule?.dropoffDateTimeTo ? dayjs(locationState.rule.dropoffDateTimeTo) : null,
                excluded: locationState?.rule?.excludedDropoffDateTimeTo
            },
            includingDateTimeFrom: {
                value: locationState?.rule?.includingDateTimeFrom ? dayjs(locationState.rule.includingDateTimeFrom) : null,
                excluded: locationState?.rule?.excludedIncludingDateTimeFrom
            },
            includingDateTimeTo: {
                value: locationState?.rule?.includingDateTimeTo ? dayjs(locationState.rule.includingDateTimeTo) : null,
                excluded: locationState?.rule?.excludedIncludingDateTimeTo
            },
            pickupLocations: {
                value: [],
                excluded: locationState?.rule?.excludedPickupLocations
            },
            dropoffLocations: {
                value: [],
                excluded: locationState?.rule?.excludedDropoffLocations
            },
            pickupAreas: {
                value: [],
                // value: locationState?.rule?.pickupAreas,
                excluded: locationState?.rule?.excludedPickupAreas
            },
            dropoffAreas: {
                value: [],
                // value: locationState?.rule?.dropoffAreas,
                excluded: locationState?.rule?.excludedDropoffAreas
            },
            pickupRegions: {
                value: [],
                // value: locationState?.rule?.pickupRegions,
                excluded: locationState?.rule?.excludedPickupRegions
            },
            dropoffRegions: {
                value: [],
                // value: locationState?.rule?.dropoffRegions,
                excluded: locationState?.rule?.excludedDropoffRegions
            },
            pickupZones: {
                value: [],
                // value: locationState?.rule?.pickupZones,
                excluded: locationState?.rule?.excludedPickupZones
            },
            dropoffZones: {
                value: [],
                // value: locationState?.rule?.dropoffZones,
                excluded: locationState?.rule?.excludedDropoffZones
            },
            acrissList: {
                value: [],
                // value: locationState?.rule?.acrissList,
                excluded: locationState?.rule?.excludedAcrissList
            },
            sippList: {
                value: [],
                // value: locationState?.rule?.sippList,
                excluded: locationState?.rule?.excludedSippList
            },
            customerCodes: {
                value: locationState?.rule?.customerCodes,
                excluded: locationState?.rule?.excludedCustomerCodes
            },
            salesChannels: {
                value: [],
                excluded: locationState?.rule?.excludedSalesChannels
            },
            segments: {
                value: [],
                excluded: locationState?.rule?.excludedSegments
            },
            categories: {
                value: [],
                excluded: locationState?.rule?.excludedCategories
            },
            products: {
                value: [],
                excluded: locationState?.rule?.excludedProducts
            },
            rates: {
                value: [],
                excluded: locationState?.rule?.excludedRates
            },
            ancillaries: {
                value: [],
                excluded: locationState?.rule?.excludedAncillaries
            },
            minDuration: {
                value: locationState?.rule?.minDuration ?? 0,
                excluded: locationState?.rule?.excludedMinDuration
            },
            maxDuration: {
                value: locationState?.rule?.maxDuration ?? 0,
                excluded: locationState?.rule?.excludedMaxDuration
            },
            minNoticeDays: {
                value: locationState?.rule?.minNoticeDays ?? 0,
                excluded: locationState?.rule?.excludedMinNoticeDays
            },
            maxNoticeHours: {
                value: locationState?.rule?.maxNoticeHours ?? 0,
                excluded: locationState?.rule?.excludedMaxNoticeHours
            },
            tenants: globalData?.user.legalEntities?.map((elem: any) => elem.name),
            dealers: globalData?.user.dealers?.map((elem: any) => elem.name)
        },
        shouldUnregister: true,
        resolver: yupResolver(ruleValidationSchema, {}, { rawValues: true })
    });

    const tenantsWatch = useWatch<IFormValuesRule, 'tenants'>({
        control: form.control,
        name: "tenants"
    });

    const dealersWatch = useWatch<IFormValuesRule, 'dealers'>({
        control: form.control,
        name: "dealers"
    });

    const createRuleMutation = useMutation(
        createRule,
        {
            onSuccess: () => {
                navigate('/booking/rules');
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    );

    const locationsQuery = useQuery({
        queryKey: [ReactQueryKey.Locations, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getLocations({tenants: tenantsWatch, dealers: dealersWatch})
    });

    const carGroupsQuery =useQuery({
        queryKey: [ReactQueryKey.CarGroups, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getCarGroups({tenants: tenantsWatch, dealers: dealersWatch}),
    });
    const segmentsQuery = useQuery({
        queryKey: [ReactQueryKey.Segments, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getSegments({tenants: tenantsWatch, dealers: dealersWatch}),
    });
    const categoriesQuery = useQuery({
        queryKey: [ReactQueryKey.Categories, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getCategories({tenants: tenantsWatch, dealers: dealersWatch}),
    });
    const productsQuery = useQuery({
        queryKey: [ReactQueryKey.Products, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getProducts({tenants: tenantsWatch, dealers: dealersWatch}),
    });
    const ratesQuery = useQuery({
        queryKey: [ReactQueryKey.Rates, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getRates({tenants: tenantsWatch, dealers: dealersWatch}),
    });
    const ancillariesQuery = useQuery({
        queryKey: [ReactQueryKey.Ancillaries, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getAncillaries({tenants: tenantsWatch, dealers: dealersWatch}),
    });
    const salesChannelsQuery = useQuery({
        queryKey: [ReactQueryKey.SalesChannels, tenantsWatch, dealersWatch],
        enabled: true,
        queryFn: () => getSalesChannels({tenants: tenantsWatch, dealers: dealersWatch})
    });

    const [constraints, setConstraints] = useState<RuleConstraint[]>(getDefaultConstraints(locationState?.rule));

    const currentDate = useMemo(() => dayjs(), []);

    const isTenantDisabling = useCallback(() => {

        return tenantsWatch?.length == 0 || dealersWatch?.length == 0;

    }, [tenantsWatch, dealersWatch])

    useEffect(() => {
        if (!isTenantDisabling()) {
            if (constraints.includes(RuleConstraint.PickupLocations)
                    || constraints.includes(RuleConstraint.DropoffLocations)
                    || constraints.includes(RuleConstraint.PickupAreas)
                    || constraints.includes(RuleConstraint.DropoffAreas)
                    || constraints.includes(RuleConstraint.PickupRegions)
                    || constraints.includes(RuleConstraint.DropoffRegions)
                    || constraints.includes(RuleConstraint.PickupZones)
                    || constraints.includes(RuleConstraint.DropoffZones)) {
                locationsQuery.refetch();
            }
            if (constraints.includes(RuleConstraint.AcrissList)
                    || constraints.includes(RuleConstraint.SippList)) {
                carGroupsQuery.refetch();
            }
            if (constraints.includes(RuleConstraint.Segments)) {
                segmentsQuery.refetch();
            }
            if (constraints.includes(RuleConstraint.Categories)) {
                categoriesQuery.refetch();
            }
            if (constraints.includes(RuleConstraint.Products)) {
                productsQuery.refetch();
            }
            if (constraints.includes(RuleConstraint.Rates)) {
                ratesQuery.refetch();
            }
            if (constraints.includes(RuleConstraint.Ancillaries)) {
                ancillariesQuery.refetch();
            }
            if (constraints.includes(RuleConstraint.SalesChannels)) {
                salesChannelsQuery.refetch();
            }
        }
    }, [constraints,tenantsWatch, dealersWatch]);

    const handleSave = useCallback((data: IFormValuesRule) => {
        if (constraints.length === 0) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t('message.error.no_rule_parameters_selected'),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            })
            return;
        }
        if (
            data.bookingDateFrom !== null
        ) {
            createRuleMutation.mutate({
                type: data.type,
                name: data.name,
                startDateTime: data.bookingDateFrom.toISOString(),
                endDateTime: data.bookingDateTo ? data.bookingDateTo.toISOString() : undefined,
                skip: data.skip,
                applyFor: data.applyFor,
                mailingList: data.mailingList ?? null,
                alertOnSetting: data.alertOnSetting !== undefined ? data.alertOnSetting : null,
                alertOnActivation: data.alertOnActivation !== undefined ? data.alertOnActivation : null,
                pickupDateTimeFrom: (data.pickupDateTimeFrom && data.pickupDateTimeFrom.value) ? {
                    value: data.pickupDateTimeFrom.value.format(LOCALE_DATE_TIME_FORMAT),
                    excluded: data.pickupDateTimeFrom.excluded
                } : undefined,
                pickupDateTimeTo: (data.pickupDateTimeTo && data.pickupDateTimeTo.value) ? {
                    value: data.pickupDateTimeTo.value.format(LOCALE_DATE_TIME_FORMAT),
                    excluded: data.pickupDateTimeTo.excluded
                } : undefined,
                dropoffDateTimeFrom: (data.dropoffDateTimeFrom && data.dropoffDateTimeFrom.value) ? {
                    value: data.dropoffDateTimeFrom.value.format(LOCALE_DATE_TIME_FORMAT),
                    excluded: data.dropoffDateTimeFrom.excluded
                } : undefined,
                dropoffDateTimeTo: (data.dropoffDateTimeTo && data.dropoffDateTimeTo.value) ? {
                    value: data.dropoffDateTimeTo.value.format(LOCALE_DATE_TIME_FORMAT),
                    excluded: data.dropoffDateTimeTo.excluded
                } : undefined,
                includingDateTimeFrom: (data.includingDateTimeFrom && data.includingDateTimeFrom.value) ? {
                    value: data.includingDateTimeFrom.value.format(LOCALE_DATE_TIME_FORMAT),
                    excluded: data.includingDateTimeFrom.excluded
                } : undefined,
                includingDateTimeTo: (data.includingDateTimeTo && data.includingDateTimeTo.value) ? {
                    value: data.includingDateTimeTo.value.format(LOCALE_DATE_TIME_FORMAT),
                    excluded: data.includingDateTimeTo.excluded
                } : undefined,
                pickupLocations: data.pickupLocations ? {
                    value: data.pickupLocations.value.map(l => l.id),
                    excluded: data.pickupLocations.excluded
                } : undefined,
                dropoffLocations: data.dropoffLocations ? {
                    value: data.dropoffLocations.value.map(l => l.id),
                    excluded: data.dropoffLocations.excluded
                } : undefined,
                pickupAreas: data.pickupAreas,
                dropoffAreas: data.dropoffAreas,
                pickupRegions: data.pickupRegions,
                dropoffRegions: data.dropoffRegions,
                pickupZones: data.pickupZones,
                dropoffZones: data.dropoffZones,
                acrissList: data.acrissList,
                sippList: data.sippList,
                customerCodes: data.customerCodes,
                salesChannels: data.salesChannels ? {
                    value: data.salesChannels.value.map(s => s.code),
                    excluded: data.salesChannels.excluded
                } : undefined,
                segments: data.segments ? {
                    value: data.segments.value.map(s => s.code),
                    excluded: data.segments.excluded
                } : undefined,
                categories: data.categories ? {
                    value: data.categories.value.map(c => c.code),
                    excluded: data.categories.excluded
                } : undefined,
                products: data.products ? {
                    value: data.products.value.map(r => r.code),
                    excluded: data.products.excluded
                } : undefined,
                rates: data.rates ? {
                    value: data.rates.value.map(r => r.code),
                    excluded: data.rates.excluded
                } : undefined,
                ancillaries: data.ancillaries ? {
                    value: data.ancillaries.value.map(a => a.code),
                    excluded: data.ancillaries.excluded
                } : undefined,
                minDuration: data.minDuration,
                maxDuration: data.maxDuration,
                minNoticeDays: data.minNoticeDays,
                maxNoticeHours: data.maxNoticeHours,
                tenants: {
                    value: data.tenants as any
                },
                dealers: {
                    value: data.dealers as any
                }
            })
        }
    }, [constraints]);

    useEffect(() => {
        if (locationsQuery.data && locationState?.rule?.pickupLocations && locationState.rule.pickupLocations.length > 0) {
            form.setValue(
                'pickupLocations.value',
                locationState.rule.pickupLocations
                    .reduce((prev: ILocation[], curr) => {
                        const founded = locationsQuery.data.locations.find(v => v.id === curr)
                        if (founded) {
                            prev.push(founded)
                        }
                        return prev;
                    }, [])
            );
        }
        if (locationsQuery.data && locationState?.rule?.dropoffLocations && locationState.rule.dropoffLocations.length > 0) {
            form.setValue(
                'dropoffLocations.value',
                locationState.rule.dropoffLocations
                    .reduce((prev: ILocation[], curr) => {
                        const founded = locationsQuery.data.locations.find(v => v.id === curr)
                        if (founded) {
                            prev.push(founded)
                        }
                        return prev;
                    }, [])
            );
        }
    }, [locationsQuery.data]);
    useEffect(() => {
        if (segmentsQuery.data && locationState?.rule?.segments && locationState.rule.segments.length > 0) {
            form.setValue(
                'segments.value',
                locationState.rule.segments
                    .reduce((prev: any[], curr) => {
                        const founded = segmentsQuery.data.list.find(v => v.code === curr)
                        if (founded) {
                            prev.push(founded)
                        }
                        return prev;
                    }, [])
            );
        }
    }, [segmentsQuery.data]);
    useEffect(() => {
        if (categoriesQuery.data && locationState?.rule?.categories && locationState.rule.categories.length > 0) {
            form.setValue(
                'categories.value',
                locationState.rule.categories
                    .reduce((prev: any[], curr) => {
                        const founded = categoriesQuery.data.list.find(v => v.code === curr)
                        if (founded) {
                            prev.push(founded)
                        }
                        return prev;
                    }, [])
            );
        }
    }, [categoriesQuery.data]);
    useEffect(() => {
        if (productsQuery.data && locationState?.rule?.products && locationState.rule.products.length > 0) {
            form.setValue(
                'products.value',
                locationState.rule.products
                    .reduce((prev: any[], curr) => {
                        const founded = productsQuery.data.list.find(v => v.code === curr)
                        if (founded) {
                            prev.push(founded)
                        }
                        return prev;
                    }, [])
            );
        }
    }, [productsQuery.data]);
    useEffect(() => {
        if (ratesQuery.data && locationState?.rule?.rates && locationState.rule.rates.length > 0) {
            form.setValue(
                'rates.value',
                locationState.rule.rates
                    .reduce((prev: any[], curr) => {
                        const founded = ratesQuery.data.list.find(v => v.code === curr)
                        if (founded) {
                            prev.push(founded)
                        }
                        return prev;
                    }, [])
            );
        }
    }, [ratesQuery.data]);
    useEffect(() => {
        if (ancillariesQuery.data && locationState?.rule?.ancillaries && locationState.rule.ancillaries.length > 0) {
            form.setValue(
                'ancillaries.value',
                locationState.rule.ancillaries
                    .reduce((prev: any[], curr) => {
                        const founded = ancillariesQuery.data.list.find(v => v.code === curr)
                        if (founded) {
                            prev.push(founded)
                        }
                        return prev;
                    }, [])
            );
        }
    }, [ancillariesQuery.data]);

    useEffect(() => {
        if (!form.formState.isSubmitting) {
            if (!_.isEmpty(form.formState.errors)) {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.form'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            }
        }
    }, [form.formState.isSubmitting]);

    const onTenantChange = useCallback(() => {
        form.setValue("dealers", []);
        form.resetField("pickupLocations.value");
        form.resetField("dropoffLocations.value");
        form.resetField("pickupAreas.value");
        form.resetField("dropoffAreas.value");
        form.resetField("pickupRegions.value");
        form.resetField("dropoffRegions.value");
        form.resetField("pickupZones.value");
        form.resetField("dropoffZones.value");
        form.resetField("acrissList.value");
        form.resetField("sippList.value");
        form.resetField("salesChannels.value");
        form.resetField("segments.value");
        form.resetField("categories.value");
        form.resetField("products.value");
        form.resetField("rates.value");
        form.resetField("ancillaries.value");
    },[])

    return (
        <>
            <SectionHeader title="Insert Rule" />
            <Stack rowGap={2} p={2}>
                <Paper elevation={1} sx={{ p: 2 }}>
                    <FormProvider {...form}>
                        <Stack rowGap={2}>
                            {/* {!(globalData?.user.legalEntities.length === 1 && globalData?.user.dealers.length === 1) && */}
                                <Grid display={userHasMultipleTenants ? undefined : "none"} container columnSpacing={3} rowSpacing={3} alignItems="start">
                                    <Grid item xs={12} lg={3}>
                                        <TenantForm 
                                            name={"tenants"} 
                                            control={form.control} 
                                            onChange={onTenantChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={3}>
                                        <DealerForm 
                                            name={"dealers"} 
                                            control={form.control} 
                                            tenants={tenantsWatch}                                    
                                        />
                                    </Grid>
                                </Grid>
                            {/* } */}
                            <FormRadioGroup<IFormValuesRule>
                                name="type"
                                label={t("label.type")}
                            >
                                <FormControlLabel
                                    value={RuleType.Pending}
                                    label={t("label.pending")}
                                    control={<Radio />}
                                    disabled={isTenantDisabling()}
                                />
                                <FormControlLabel
                                    value={RuleType.StopSells}
                                    label={t("label.stopSell")}
                                    control={<Radio />}
                                    disabled={isTenantDisabling()}
                                />
                            </FormRadioGroup>
                            <Grid container columnSpacing={3} rowSpacing={3} alignItems="start">
                                <Grid item xs={12} lg={3}>
                                    <FormTextField<IFormValuesRule>
                                        name="name"
                                        label={t("label.name")}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormDateTimePicker<IFormValuesRule>
                                        name="bookingDateFrom"
                                        label={t("label.bookingDateFrom")}
                                        minDateTime={currentDate}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormDateTimePicker<IFormValuesRule>
                                        name="bookingDateTo"
                                        label={t("label.bookingDateTo")}
                                        minDateTime={currentDate}
                                    />
                                </Grid>
                                <Grid item xs={12} lg={3}>
                                    <FormNumberInput<IFormValuesRule>
                                        name="skip"
                                        label={t("label.skip")}
                                        minValue={0}
                                    />
                                </Grid>
                                {form.watch('type') === RuleType.Pending &&
                                    <Grid item xs={12} lg={3}>
                                        <FormNumberInput<IFormValuesRule>
                                            name="applyFor"
                                            label={t("label.applyFor")}
                                            minValue={0}
                                        />
                                    </Grid>
                                }
                            </Grid>
                            {form.watch('type') === RuleType.StopSells &&
                                <Grid container columnSpacing={3} rowSpacing={3} alignItems="end">
                                    <Grid item xs={12} lg={3}>
                                        <MailingList />
                                    </Grid>
                                    <Grid item xs={12} lg={3}>
                                        <FormCheckbox<IFormValuesRule>
                                            name="alertOnSetting"
                                            label={t("label.alertOnSetting")}
                                        />
                                    </Grid>
                                    <Grid item xs={12} lg={3}>
                                        <FormCheckbox<IFormValuesRule>
                                            name="alertOnActivation"
                                            label={t("label.alertOnActivation")}
                                        />
                                    </Grid>
                                </Grid>
                            }
                            <RulesProvider
                                constraints={constraints}
                                setConstraints={setConstraints}
                            >
                                <ConstraintList 
                                    creationDisabled={isTenantDisabling()}
                                />
                            </RulesProvider>
                        </Stack>
                    </FormProvider>
                </Paper>
                <Stack direction="row" justifyContent="end">
                    <Button onClick={form.handleSubmit(handleSave)}>
                        {t("label.save")}
                    </Button>
                </Stack>
            </Stack>
            <CustomLoader loading={createRuleMutation.isLoading} />
        </>
    )
};

export default InsertRulePage;